import {
    Fingerprint,
    KeyRound,
    Loader2,
    LogOut,
    UserRound,
} from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
    OAUTH_PROVIDER_NAMES,
    OAuthProviderLogo,
} from '@/features/auth/components/OAuthProviderLogo';
import { useOAuthSession } from '@/features/auth/hooks/useOAuthSession';
import { useSignInDialogStore } from '@/features/auth/signInDialogStore';
// the component module only: the flow barrel also carries the sign-out
// dialog, which imports the logout routine.
import FlowToggle from '@/features/flow/components/FlowToggle';
// the component module only: the migration barrel also carries its dialog.
import MigrateButton from '@/features/migration/components/MigrateButton';
// deep import (not the feature barrel) to avoid a wallet<->tradingSession import
// cycle — the store is a standalone zustand slice with no wallet imports.
import { useTradingSessionStore } from '@/features/tradingSession/store';
import { useSessionResolved } from '@/features/tradingSession/useSessionResolved';
import { cn, shortenAddress } from '@/lib/utils';

import { useAuth, useWallet } from '../hooks';
import { useWalletStore } from '../store';

interface WalletConnectControlProps {
    className?: string;
    compact?: boolean;
}

function shortestIdentityLabel(
    ...values: Array<string | null | undefined>
): string | null {
    const candidates = values
        .map((value) => value?.trim())
        .filter((value): value is string => !!value);
    if (candidates.length === 0) return null;
    return candidates.reduce((shortest, value) =>
        value.length < shortest.length ? value : shortest,
    );
}

/**
 * Account/session control. Wallet connection lives inside EIP/deposit
 * ceremonies so normal OAuth and passkey trading never depends on MetaMask.
 */
const WalletConnectControl: React.FC<WalletConnectControlProps> = ({
    className,
    compact = false,
}) => {
    const {
        isAuthenticated,
        hasTradingAuthority,
        authorityMethod,
        isAuthenticating,
        login,
        logout,
    } = useAuth();
    const {
        isAuthenticated: isOAuthAuthenticated,
        provider: oauthProvider,
        displayName,
        username,
    } = useOAuthSession();
    const openOAuthAccount = useSignInDialogStore(
        (state) => state.openOAuthAccount,
    );
    const openAccount = useSignInDialogStore((state) => state.openAccount);
    // same gate the old floating launcher used: a live app session, nothing else.
    const hasAppSession = useWalletStore(
        (state) => !!state.session && state.session.expiresAt > Date.now(),
    );
    // the trading window's owner is authoritative for the eip712 label; the live
    // wagmi address only fills in for sessions minted before owner correlation.
    const sessionOwner = useTradingSessionStore((state) =>
        state.status?.state === 'active' ? (state.status.owner ?? null) : null,
    );
    const { address: connectedAddress } = useWallet();
    const sessionResolved = useSessionResolved();

    if (!sessionResolved) {
        // "Sign in" is a claim about the user, and until the first status read
        // lands it is usually a wrong one. Hold the space, show nothing.
        return (
            <div
                aria-hidden="true"
                className={cn('invisible inline-flex items-center', className)}
            />
        );
    }

    let methodLabel = 'Account';
    if (authorityMethod === 'passkey') methodLabel = 'Passkey';
    if (authorityMethod === 'eip712') methodLabel = 'Connected Wallet';
    if (authorityMethod === 'oauth') methodLabel = 'OAuth';

    const ownerAddress = sessionOwner ?? connectedAddress ?? null;
    const shortOwner = ownerAddress ? shortenAddress(ownerAddress) : null;

    const oauthIdentityLabel = shortestIdentityLabel(username, displayName);
    const oauthProviderName = oauthProvider
        ? OAUTH_PROVIDER_NAMES[oauthProvider]
        : 'OAuth';
    const hasOAuthIdentity = isOAuthAuthenticated && !!oauthProvider;
    const isOAuthAuthority = isAuthenticated && authorityMethod === 'oauth';
    const showOAuthAsPrimary =
        isOAuthAuthority || (!hasTradingAuthority && hasOAuthIdentity);
    // the app token deliberately outlives the trading session (non-sensitive reads keep working), so
    // it alone never proves somebody is signed in. this ONLY decides the logged-out label — any
    // identity, oauth or trading authority, keeps the previous display untouched.
    const hasRealIdentity =
        isAuthenticated && (hasTradingAuthority || hasOAuthIdentity);
    // a social login that lost its wallet binding (unlink) has no app token and
    // no trading authority, but its cookie is still live and its name is still
    // on the button: it must be able to leave. Sign out is offered for ANY
    // identity, not only a fully authenticated one.
    const canSignOut = hasRealIdentity || hasOAuthIdentity;

    let label = 'Sign in';
    if (showOAuthAsPrimary) {
        label =
            oauthIdentityLabel ??
            (oauthProvider ? oauthProviderName : 'Signed in');
    } else if (hasRealIdentity) {
        // wallet-backed authorities show WHICH owner EOA holds them
        // ("0x1234…abcd wallet" / "0x1234…abcd passkey") instead of the
        // opaque "... active" copy; without a known owner the old copy remains.
        if (authorityMethod === 'eip712' && shortOwner) {
            label = compact ? shortOwner : `${shortOwner} wallet`;
        } else if (authorityMethod === 'passkey' && shortOwner) {
            label = compact ? methodLabel : `${shortOwner} passkey`;
        } else {
            label = compact ? methodLabel : `${methodLabel} active`;
        }
    }

    const accessibleLabel = showOAuthAsPrimary
        ? `${oauthProviderName} account: ${label}`
        : label;

    // there is NEVER a standalone Passkeys button — management is always behind
    // the identity button itself: OAuth-primary identities reach it inside the
    // account window (SignInDialog's oauth-account view); a signed-in
    // wallet-backed identity's button opens its own account window (re-showing
    // the sign-in chooser to someone already signed in helped nobody). that
    // window is also where a wallet-first user, who has no social login and no
    // other surface offering one, can add one to the account they already own.
    // a live app session is still required — the app token outlives trading
    // authority, and neither belongs behind a "Sign in" button.
    const opensAccountWindow =
        hasAppSession && hasRealIdentity && !showOAuthAsPrimary;

    let Icon = UserRound;
    if (authorityMethod === 'passkey') Icon = Fingerprint;
    if (authorityMethod === 'eip712') Icon = KeyRound;

    return (
        <div className="inline-flex items-center gap-1">
            <Button
                variant="outline"
                size={compact ? 'sm' : 'default'}
                disabled={isAuthenticating}
                onClick={() =>
                    showOAuthAsPrimary
                        ? openOAuthAccount()
                        : opensAccountWindow
                            ? openAccount()
                            : void login()
                }
                aria-label={accessibleLabel}
                {...(opensAccountWindow ? { title: 'Your account' } : {})}
                className={cn(className)}
            >
                {isAuthenticating ? (
                    <Loader2 className="size-3.5 animate-spin" />
                ) : showOAuthAsPrimary && oauthProvider ? (
                    <OAuthProviderLogo
                        provider={oauthProvider}
                        className="size-3.5"
                    />
                ) : (
                    <Icon className="size-3.5" />
                )}
                <span
                    className={cn(
                        'font-mono text-xs',
                        showOAuthAsPrimary &&
                            (compact
                                ? 'max-w-20 truncate'
                                : 'max-w-32 truncate'),
                    )}
                >
                    {label}
                </span>
            </Button>

            {!showOAuthAsPrimary && hasOAuthIdentity && (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={openOAuthAccount}
                    className="size-8 text-muted-foreground hover:text-foreground"
                    title={`Manage ${oauthProviderName} account`}
                    aria-label={`Manage ${oauthProviderName} account`}
                >
                    <OAuthProviderLogo
                        provider={oauthProvider}
                        className="size-3.5"
                    />
                </Button>
            )}

            {/* V1/V2, only when both flows are unlocked for the account */}
            <FlowToggle compact={compact} />

            {/* V1 in use, both unlocked, a V1 balance: move it to the Snappy Wallet */}
            <MigrateButton compact={compact} />

            {canSignOut && !compact && (
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={(event) => {
                        event.stopPropagation();
                        void logout().catch(() => undefined);
                    }}
                    className="size-8 text-muted-foreground hover:text-foreground"
                    title="Sign out"
                    aria-label="Sign out"
                >
                    <LogOut className="size-3.5" />
                </Button>
            )}
        </div>
    );
};

WalletConnectControl.displayName = 'WalletConnectControl';

export default WalletConnectControl;
